// src/pages/ListaClientes.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { listarClientesPaginado, excluirCliente } from "../services/api";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  CheckCircle,
  XCircle,
  Edit,
  Trash2,
  Search,
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
  ArrowUpDown,
} from "lucide-react";

const HeaderSortButton = ({ label, field, sortBy, desc, onSort }) => {
  const active = sortBy === field;

  return (
    <button
      type="button"
      className={`
        inline-flex items-center gap-1 transition
        ${active ? "text-blue-600 dark:text-blue-300" : "text-gray-600 dark:text-slate-200"}
        hover:text-blue-700 dark:hover:text-blue-200
      `}
      onClick={() => onSort(field)}
      title="Ordenar"
    >
      {label} <ArrowUpDown size={16} />
      {active && <span className="text-xs ml-1 opacity-80">{desc ? "↓" : "↑"}</span>}
    </button>
  );
};

const LicencaIcon = ({ value }) =>
  value ? (
    <CheckCircle className="text-green-500 inline" size={20} />
  ) : (
    <XCircle className="text-red-500 inline" size={20} />
  );

const ListaClientes = ({ token }) => {
  const navigate = useNavigate();

  // URL params (igual ZenTag, mas SEM revendaId)
  const [searchParams, setSearchParams] = useSearchParams();
  const qp = (k, def = "") => searchParams.get(k) ?? def;

  // filtros
  const [busca, setBusca] = useState(qp("search", ""));

  // paginação/ordenação
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(Number(qp("page", "1")) || 1);
  const [pageSize, setPageSize] = useState(Number(qp("pageSize", "60")) || 60);

  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [hasPrevious, setHasPrevious] = useState(false);
  const [hasNext, setHasNext] = useState(false);

  const [sortBy, setSortBy] = useState(qp("sortBy", "razaosocial"));
  const [desc, setDesc] = useState(qp("desc", "") === "true");

  // debounce busca
  const [searchTerm, setSearchTerm] = useState(qp("search", ""));
  const debounceRef = useRef(null);

  // sincroniza filtros/paginação na URL (SEM revenda)
  useEffect(() => {
    const p = new URLSearchParams(searchParams);

    if (busca) p.set("search", busca);
    else p.delete("search");

    p.set("page", String(page));
    p.set("pageSize", String(pageSize));

    if (sortBy) p.set("sortBy", sortBy);
    if (desc) p.set("desc", "true");
    else p.delete("desc");

    setSearchParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busca, page, pageSize, sortBy, desc]);

  const fetchData = async (opts = {}) => {
    // IMPORTANT: não manda revendaId, backend aplica pelo token
    const res = await listarClientesPaginado(
      {
        page,
        pageSize,
        search: searchTerm,
        sortBy,
        desc,
        ...opts,
      },
      token
    );

    const data = res.data;
    setItems(data.items || []);
    setPage(data.page);
    setPageSize(data.pageSize);
    setTotalItems(data.totalItems);
    setTotalPages(data.totalPages);
    setHasPrevious(data.hasPrevious);
    setHasNext(data.hasNext);
  };

  useEffect(() => {
    fetchData().catch((err) => console.error("Erro ao listar clientes:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, searchTerm, sortBy, desc]);

  const handleBuscaChange = (e) => {
    const valor = e.target.value;
    setBusca(valor);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      setSearchTerm(valor);
    }, 400);
  };

  const handleSort = (field) => {
    if (sortBy === field) setDesc((d) => !d);
    else {
      setSortBy(field);
      setDesc(false);
    }
    setPage(1);
  };

  const handleEditar = (id) => navigate(`/clientes/editar/${id}`);

  const handleExcluir = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este cliente?")) return;
    try {
      await excluirCliente(id, token);
      await fetchData();
      alert("Cliente excluído com sucesso!");
    } catch (error) {
      console.error("Erro ao excluir cliente:", error);
      alert("Erro ao excluir cliente!");
    }
  };

  const paginaInfo = useMemo(() => {
    const ini = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
    const fim = Math.min(page * pageSize, totalItems);
    return `${ini}-${fim} de ${totalItems}`;
  }, [page, pageSize, totalItems]);

  return (
    <div className="zen-card rounded-2xl p-6 shadow-sm dark:backdrop-blur">
      {/* topo */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Listagem de Clientes
          </h2>
          <p className="text-xs text-gray-500 dark:text-slate-300/80 mt-1">
            Listando apenas clientes do seu escopo (revenda do token).
          </p>
        </div>
      </div>

      {/* filtros */}
      <div className="rounded-2xl p-4 mb-6 zen-card border-gray-200 dark:bg-white/5 dark:border-white/10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          {/* busca */}
          <div className="md:col-span-9">
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
              Buscar
            </label>
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-400 pointer-events-none"
                size={18}
              />
              <input
                type="text"
                placeholder="Buscar por CNPJ / Razão / Nome Fantasia"
                value={busca}
                onChange={handleBuscaChange}
                className="
                  pl-10 pr-4 py-2.5 w-full rounded-xl border outline-none transition
                  zen-card text-gray-900 placeholder:text-gray-400
                  focus:ring-2 focus:ring-blue-500/60
                  dark:bg-slate-950/40 dark:border-white/10 dark:text-white dark:placeholder:text-slate-400
                  dark:focus:ring-blue-400/40
                "
              />
            </div>
          </div>

          {/* page size */}
          <div className="md:col-span-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">
              Itens por página
            </label>
            <select
              value={pageSize}
              onChange={(e) => {
                setPage(1);
                setPageSize(Number(e.target.value));
              }}
              className="
                w-full p-2.5 rounded-xl border outline-none transition
                zen-card border-gray-300 text-gray-900
                focus:ring-2 focus:ring-blue-500/60
                dark:bg-slate-950/40 dark:border-white/10 dark:text-white
                dark:focus:ring-blue-400/40
              "
            >
              {[10, 20, 30, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}/página
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* tabela */}
      <div className="overflow-x-auto">
        <div className="min-w-full rounded-2xl zen-card dark:border-white/10 dark:bg-white/5">
          <table className="min-w-full">
            <thead className="bg-gray-50 dark:bg-white/5">
              <tr>
                <th className="py-3 px-4 text-left font-semibold text-gray-600 dark:text-slate-200">
                  <HeaderSortButton label="CNPJ" field="cnpj" sortBy={sortBy} desc={desc} onSort={handleSort} />
                </th>
                <th className="py-3 px-4 text-left font-semibold text-gray-600 dark:text-slate-200">
                  <HeaderSortButton label="Razão Social" field="razaosocial" sortBy={sortBy} desc={desc} onSort={handleSort} />
                </th>
                <th className="py-3 px-4 text-left font-semibold text-gray-600 dark:text-slate-200">
                  <HeaderSortButton label="Nome Fantasia" field="nomefantasia" sortBy={sortBy} desc={desc} onSort={handleSort} />
                </th>
                <th className="text-center py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">ZenPDV</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">ZenMobile</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">ZenGestão</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">ZenTag</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">ZenPass</th>
                <th className="text-center py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">Ações</th>
              </tr>
            </thead>

            <tbody className="text-gray-700 dark:text-slate-200">
              {items.map((c) => (
                <tr
                  key={c.id}
                  className="border-t border-gray-200 hover:bg-gray-50 transition dark:border-white/10 dark:hover:bg-white/5"
                >
                  <td className="py-3 px-4">{c.cnpj}</td>
                  <td className="py-3 px-4">{c.razaoSocial}</td>
                  <td className="py-3 px-4">{c.nomeFantasia}</td>

                  <td className="text-center py-3 px-4"><LicencaIcon value={c.zenPDV} /></td>
                  <td className="text-center py-3 px-4"><LicencaIcon value={c.zenMobile} /></td>
                  <td className="text-center py-3 px-4"><LicencaIcon value={c.zenGestao} /></td>
                  <td className="text-center py-3 px-4"><LicencaIcon value={c.zenTag} /></td>
                  <td className="text-center py-3 px-4"><LicencaIcon value={c.zenPass} /></td>

                  <td className="text-center py-3 px-4">
                    <div className="inline-flex items-center gap-1">
                      <button
                        className="p-2 rounded-full transition text-blue-600 hover:bg-blue-100 dark:text-blue-300 dark:hover:bg-blue-500/10"
                        onClick={() => handleEditar(c.id)}
                        title="Editar"
                      >
                        <Edit size={18} />
                      </button>

                      <button
                        className="p-2 rounded-full transition text-red-600 hover:bg-red-100 dark:text-red-300 dark:hover:bg-red-500/10"
                        onClick={() => handleExcluir(c.id)}
                        title="Excluir"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {items.length === 0 && (
                <tr>
                  <td colSpan="8" className="py-10 text-center text-gray-500 dark:text-slate-300/80">
                    Nenhum cliente encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* paginação */}
      <div className="mt-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <span className="text-sm text-gray-600 dark:text-slate-300/80">{paginaInfo}</span>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setPage(1)}
            disabled={!hasPrevious}
            className="
              p-2 rounded-xl border transition
              bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200
              disabled:opacity-50 disabled:cursor-not-allowed
              dark:bg-white/5 dark:text-white dark:border-white/10 dark:hover:bg-white/10
            "
            title="Primeira"
          >
            <ChevronsLeft size={18} />
          </button>

          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={!hasPrevious}
            className="
              p-2 rounded-xl border transition
              bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200
              disabled:opacity-50 disabled:cursor-not-allowed
              dark:bg-white/5 dark:text-white dark:border-white/10 dark:hover:bg-white/10
            "
            title="Anterior"
          >
            <ChevronLeft size={18} />
          </button>

          <span className="px-2 text-sm text-gray-700 dark:text-slate-200">
            Página <b>{page}</b> de <b>{totalPages}</b>
          </span>

          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={!hasNext}
            className="
              p-2 rounded-xl border transition
              bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200
              disabled:opacity-50 disabled:cursor-not-allowed
              dark:bg-white/5 dark:text-white dark:border-white/10 dark:hover:bg-white/10
            "
            title="Próxima"
          >
            <ChevronRight size={18} />
          </button>

          <button
            onClick={() => setPage(totalPages)}
            disabled={!hasNext}
            className="
              p-2 rounded-xl border transition
              bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-200
              disabled:opacity-50 disabled:cursor-not-allowed
              dark:bg-white/5 dark:text-white dark:border-white/10 dark:hover:bg-white/10
            "
            title="Última"
          >
            <ChevronsRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ListaClientes;
